'use strict';

(() => {
  const DEFAULT_MAX_BADGES = 2;

  function toBool(value) {
    return value === true || value === 'true' || value === 'True' || value === 1 || value === '1';
  }

  function unwrapMeta(value) {
    if (value && typeof value === 'object' && 'value' in value) {
      return value.value;
    }

    return value;
  }

  function readMetaValue(primary, fallback) {
    const primaryValue = unwrapMeta(primary);
    const fallbackValue = unwrapMeta(fallback);

    if (primaryValue === undefined || primaryValue === null || primaryValue === '')
      return fallbackValue;
    return primaryValue;
  }

  function normalizeOfferValue(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function normalizeTags(tags) {
    if (Array.isArray(tags)) {
      return tags.map((t) => String(t).toLowerCase());
    }

    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    }

    return [];
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function readVariantMeta(variant) {
    const customMeta = variant?.metafields?.custom || {};
    const rootMeta = variant?.metafields || {};

    return {
      enableFlashSale: readMetaValue(customMeta.enable_flash_sale, rootMeta.enable_flash_sale),
      allowExpressShipping: readMetaValue(
        customMeta.allow_express_shipping,
        rootMeta.allow_express_shipping
      ),
      brightperlInventory:
        readMetaValue(customMeta.brightperl_variant_inventory, rootMeta.brightperl_inventory) || '',
      isOfferApplicable: readMetaValue(
        customMeta.is_offer_applicable,
        rootMeta.is_offer_applicable
      ),
    };
  }

  function isVariantAvailable(variant) {
    const available = variant?.available;
    return available === true || available === 1 || available === '1' || available === 'true';
  }

  function findSelectedVariant(variants, variantId) {
    if (!variants?.length) return null;
    if (!variantId) return variants[0];

    return variants.find((v) => String(v.id) === String(variantId)) || variants[0];
  }

  function hasOfferVariant(variants) {
    return variants.some((v) => {
      if (!isVariantAvailable(v)) return false;
      const meta = readVariantMeta(v);
      return toBool(meta.isOfferApplicable);
    });
  }

  function parseMiramarInventory(inventoryRaw, threshold) {
    const inventory = String(inventoryRaw || '');
    if (!inventory) return false;

    return inventory.split(',').some((item) => {
      const [qty, location] = item.trim().split('_');
      return location === 'Miramar' && Number(qty) > threshold;
    });
  }

  function getMaxDiscount(variants) {
    let max = 0;

    for (const v of variants) {
      const compareAt = toNumber(v.compare_at_price, 0);
      const price = toNumber(v.price, 0);
      const discount = compareAt - price;
      if (discount > max) max = discount;
    }

    return max;
  }

  function canShowExpressShipping(variant, config) {
    if (!config.expressEnabled) return false;
    if (!isVariantAvailable(variant)) return false;

    const meta = readVariantMeta(variant);

    if (meta.allowExpressShipping === true || meta.allowExpressShipping === 'true') return true;
    if (meta.allowExpressShipping === false || meta.allowExpressShipping === 'false') return false;

    return parseMiramarInventory(meta.brightperlInventory, config.expressThreshold);
  }

  function useVariantLevel(ctx) {
    return ctx.config.badgeMode === 'variant-level' || ctx.config.isOfferCollection;
  }

  function getFlashSaleBadge(ctx) {
    const { selectedVariant, config } = ctx;
    if (!selectedVariant) return '';

    const meta = readVariantMeta(selectedVariant);
    const isEligible = (
      isVariantAvailable(selectedVariant) &&
      toBool(meta.enableFlashSale) &&
      config.flashSaleTimerActive &&
      config.flashSaleExtensionActive
    );

    if (!isEligible) return '';

    return `<div class="product-card__badge badge--flash-sale" role="img" aria-label="Flash sale">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/flash-sale.svg?v=1753779044" width="12" height="12" loading="lazy" alt="" aria-hidden="true">
              <span aria-hidden="true">Flash sale</span>
            </div>`;
  }

  function shouldShowExpressShipping(ctx) {
    const { selectedVariant, variants, config } = ctx;

    if (useVariantLevel(ctx)) {
      return Boolean(selectedVariant && canShowExpressShipping(selectedVariant, config));
    }

    return variants.some((variant) => canShowExpressShipping(variant, config));
  }

  function getExpressShippingBadge(ctx) {
    if (!shouldShowExpressShipping(ctx)) return '';

    return `<div class="product-card__badge badge--express-shipping" role="img" aria-label="${escapeHtml(ctx.config.deliveryDays)} Day Shipping">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/shipping-icon_39109e29-bab1-43f2-9863-f8ab04a5ce71.svg?v=1759829562" width="12" height="12" loading="lazy" alt="" aria-hidden="true">
              <span aria-hidden="true">${escapeHtml(ctx.config.deliveryDays)} Day Shipping</span>
            </div>`;
  }

  function getBundleOfferBadge(ctx) {
    const normalizedOffer = normalizeOfferValue(ctx.config.productOffer || '');
    const map = {
      'bogo 50': { className: 'badge--bogo-50', label: 'BOGO 50 - Mix & Match' },
      '2 for $25': { className: 'badge--2-for-25', label: '2 for $25 - Mix & Match' },
      '2 for $75': { className: 'badge--2-for-75', label: '2 for $75 - Mix & Match' },
      '3 for $20': { className: 'badge--3-for-20', label: '3 for $20 - Mix & Match' },
      '3 for $99': { className: 'badge--3-for-99', label: '3 for $99 - Mix & Match' },
      '3 for $129': { className: 'badge--3-for-129', label: '3 for $129 - Mix & Match' },
    };

    const offerData = map[normalizedOffer];
    if (!offerData) return '';

    const selectedVariantEligible =
      isVariantAvailable(ctx.selectedVariant) &&
      toBool(readVariantMeta(ctx.selectedVariant).isOfferApplicable);

    const anyVariantEligible = ctx.variants.some((variant) => {
      if (!isVariantAvailable(variant)) return false;
      return toBool(readVariantMeta(variant).isOfferApplicable);
    });

    const showOffer = useVariantLevel(ctx) ? selectedVariantEligible : anyVariantEligible;

    if (!showOffer) return '';

    return `<div class="product-card__badge ${offerData.className}" aria-label="${escapeHtml(offerData.label)}">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/offer.svg?v=1722427324" width="12" height="12" loading="lazy" alt="" aria-hidden="true" role="presentation">
              <span aria-hidden="true">${escapeHtml(offerData.label)}</span>
            </div>`;
  }

  function getDiscountBadge(ctx) {
    if (!isVariantAvailable(ctx.selectedVariant)) return '';
    const maxDiscount = getMaxDiscount(ctx.variants);
    if (maxDiscount <= 0) return '';

    const value = Math.floor(maxDiscount / 100) + 1;

    return `<div class="product-card__badge badge--max-discount" role="img" aria-label="Up to $${escapeHtml(value)} Off">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/offer.svg?v=1722427324" width="12" height="12" loading="lazy" alt="" aria-hidden="true">
              <span aria-hidden="true">Up to $${escapeHtml(value)} Off</span>
            </div>`;
  }

  function getBestSellerBadge(ctx) {
    if (!ctx.tags.includes('best-sellers')) return '';

    return `<div class="product-card__badge badge--best-seller" role="img" aria-label="Best Seller">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/Best_Seller_Tag.svg?v=1722427276" width="12" height="12" loading="lazy" alt="" aria-hidden="true">
              <span aria-hidden="true">Best Seller</span>
            </div>`;
  }

  function getOnlineOnlyBadge(ctx) {
    if (!ctx.tags.includes('online-only-item')) return '';

    return `<div class="product-card__badge badge--online-only" role="img" aria-label="Online Only">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/Group_2469.svg?v=1724309634" width="12" height="12" loading="lazy" alt="" aria-hidden="true">
              <span aria-hidden="true">Online Only</span>
            </div>`;
  }

  function isProductAvailable(product, variants) {
    if (product?.available === false) return false;
    return variants.some(isVariantAvailable);
  }

  function getSoldOutBadge(ctx) {
    if (isProductAvailable(ctx.product, ctx.variants)) return '';

    return `<div class="product-card__badge badge--sold-out">
              <img src="https://cdn.shopify.com/s/files/1/0269/7763/2389/files/icons8-out-of-stock.png?v=1783414775" width="16" height="16" loading="lazy" alt="" aria-hidden="true" role="presentation">
              <span>Out Of Stock</span>
            </div>`;
  }

  function getBadges(ctx) {
    const badgesHtml = [];
    const max = ctx.config.maxBadges;

    function push(html) {
      if (!html || badgesHtml.length >= max) return;
      badgesHtml.push(html);
    }

    push(getSoldOutBadge(ctx));
    push(getFlashSaleBadge(ctx));
    push(getExpressShippingBadge(ctx));
    push(getBundleOfferBadge(ctx));
    push(getDiscountBadge(ctx));
    push(getBestSellerBadge(ctx));
    push(getOnlineOnlyBadge(ctx));

    return badgesHtml.slice(0, max).join('');
  }

  class ProductBadgesComponent extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-product-badges-root]');
      this.jsonNode = this.querySelector('[data-product-badges-json]');
      this.variantMetaNode = this.querySelector('[data-product-badges-variant-meta-json]');
      if (!this.root || !this.jsonNode?.textContent) return;

      try {
        this.product = JSON.parse(this.jsonNode.textContent);
      } catch (error) {
        console.error('product-badges-component: invalid product JSON', error);
        return;
      }

      this.productId = this.getAttribute('data-product-id') || this.product?.id || '';
      this.selectedVariantId = this.getAttribute('data-selected-variant-id') || '';
      this.variantMetaMap = this.parseVariantMetaMap();

      this.config = {
        maxBadges: toNumber(this.getAttribute('data-max-badges'), DEFAULT_MAX_BADGES),
        flashSaleExtensionActive: toBool(this.getAttribute('data-flash-sale-extension')),
        flashSaleTimerActive: toBool(this.getAttribute('data-flash-sale-timer')),
        expressEnabled: toBool(this.getAttribute('data-express-shipping-enabled')),
        expressThreshold: toNumber(this.getAttribute('data-express-shipping-threshold'), 2),
        deliveryDays: this.getAttribute('data-delivery-days') || '2',
        productOffer: this.getAttribute('data-product-offer') || '',
        isOfferCollection: toBool(this.getAttribute('data-is-offer-collection')),
        badgeMode: this.getAttribute('data-badge-mode') || 'product-level',
      };

      this.handleVariantChange = this.onVariantChange.bind(this);
      document.addEventListener('variant:changed', this.handleVariantChange);

      this.render();
    }

    parseVariantMetaMap() {
      if (!this.variantMetaNode?.textContent) return new Map();

      try {
        const data = JSON.parse(this.variantMetaNode.textContent);
        if (!Array.isArray(data)) return new Map();

        const map = new Map();

        for (const item of data) {
          const id = item?.id;
          if (id == null) continue;

          map.set(String(id), {
            enable_flash_sale: unwrapMeta(item.enable_flash_sale),
            allow_express_shipping: unwrapMeta(item.allow_express_shipping),
            brightperl_variant_inventory: unwrapMeta(item.brightperl_variant_inventory),
            is_offer_applicable: unwrapMeta(item.is_offer_applicable),
          });
        }

        return map;
      } catch (error) {
        console.error('product-badges-component: invalid variant meta JSON', error);
        return new Map();
      }
    }

    hydrateVariants(variants) {
      if (!variants?.length || !this.variantMetaMap?.size) return variants || [];

      return variants.map((variant) => {
        const externalMeta = this.variantMetaMap.get(String(variant.id));
        if (!externalMeta) return variant;

        const existingMetafields = variant.metafields || {};
        const existingCustom = existingMetafields.custom || {};

        return {
          ...variant,
          metafields: {
            ...existingMetafields,
            custom: {
              ...existingCustom,
              enable_flash_sale: readMetaValue(
                existingCustom.enable_flash_sale,
                externalMeta.enable_flash_sale
              ),
              allow_express_shipping: readMetaValue(
                existingCustom.allow_express_shipping,
                externalMeta.allow_express_shipping
              ),
              brightperl_variant_inventory: readMetaValue(
                existingCustom.brightperl_variant_inventory,
                externalMeta.brightperl_variant_inventory
              ),
              is_offer_applicable: readMetaValue(
                existingCustom.is_offer_applicable,
                externalMeta.is_offer_applicable
              ),
            },
          },
        };
      });
    }

    disconnectedCallback() {
      if (this.handleVariantChange) {
        document.removeEventListener('variant:changed', this.handleVariantChange);
      }
    }

    onVariantChange(event) {
      const detail = event?.detail;
      if (!detail) return;

      const { productId, variantId } = detail;
      if (String(productId) !== String(this.productId)) return;

      this.selectedVariantId = variantId;
      // console.log('[product-badges] variant:changed', {
      //   productId: this.productId,
      //   variantId: this.selectedVariantId,
      // });
      this.render();
    }

    buildContext() {
      const variants = this.hydrateVariants(this.product?.variants || []);
      const selectedVariant = findSelectedVariant(variants, this.selectedVariantId);

      return {
        product: this.product,
        variants,
        selectedVariant,
        tags: normalizeTags(this.product?.tags),
        config: this.config,
      };
    }

    render() {
      const ctx = this.buildContext();
      const selectedMeta = ctx.selectedVariant ? readVariantMeta(ctx.selectedVariant) : null;
      const expressEligible = shouldShowExpressShipping(ctx);
      const badgesHtml = getBadges(ctx);

      // console.log('[product-badges] render', {
      //   productId: this.productId,
      //   selectedVariantId: ctx.selectedVariant?.id || null,
      //   selectedVariantAvailable: ctx.selectedVariant?.available ?? null,
      //   selectedVariantAvailableNormalized: isVariantAvailable(ctx.selectedVariant),
      //   selectedVariantMeta: selectedMeta,
      //   expressEnabledSetting: ctx.config.expressEnabled,
      //   expressThreshold: ctx.config.expressThreshold,
      //   expressEligible,
      //   productOfferRaw: ctx.config.productOffer,
      //   productOfferNormalized: normalizeOfferValue(ctx.config.productOffer),
      //   productOffer: ctx.config.productOffer,
      //   hasOfferVariant: hasOfferVariant(ctx.variants),
      //   offerVariants: ctx.variants.map((v) => ({
      //     id: v.id,
      //     available: v.available,
      //     availableNormalized: isVariantAvailable(v),
      //     isOfferApplicable: readVariantMeta(v).isOfferApplicable,
      //     isOfferApplicableNormalized: toBool(readVariantMeta(v).isOfferApplicable),
      //   })),
      //   badgesHtml,
      // });

      this.root.innerHTML = badgesHtml;
    }
  }
  if (!customElements.get('product-badges-component')) {
    customElements.define('product-badges-component', ProductBadgesComponent);
  }
})();
